<template>
    <PageWrapper
        :class="additionalClass"
        non-destructive-on-loading
        title="Actions Queue"
        :description="description"
        :loading="isLoading"
    >
        <div class="ActionsReview__container">
            <ActionReviewDetails
                v-if="currentAction"
                :key="currentAction.id"
                :action-review="currentAction"
                :read-only="isCurrentActionReadOnly"
                :current-position="currentActionIndex == null ? null : currentActionIndex + 1"
                :max-position="entityActionsStatus.list.length"
                :endorsement-categories="endorsementCategoriesStatus.list"
                @back="navigateBack"
                @previous="previousAction"
                @next="nextAction"
                @resolve="tryResolveAction"
                @revert="openRevertModal"
            />
            <div v-else class="ActionsReview__tableSection">
                <div v-if="hasProcessingContracts" class="ActionsReview__processingMessage">
                    <EidIcon class="ActionsReview__processingSpinner animate-spin" :icon-src="faSpinner" />
                    {{ processingContractsMessage }}
                </div>
                <Tabs class="ActionsReview__tabs" :selected-tab="activeTab" @change="onTabChange">
                    <Tab
                        v-for="tabId of tabIds"
                        :id="tabId"
                        :key="tabId"
                        :name="tabLabels[tabId]"
                    >
                        <ActionsList
                            v-if="activeTab === tabId"
                            :tab="tabId"
                            :rp-name="rpName"
                            :filters="filters"
                            :sorting="sorting"
                            :requirement-definitions="requirementDefinitions"
                            :entity-name="entityName"
                            :no-action-message="noActionMessage"
                            :reload-token="reloadToken"
                            @review="navigateToDetails($event)"
                            @sort-change="onSortChange"
                            @remove-filter="removeFilter"
                            @filters-change="onFiltersChange"
                        />
                    </Tab>
                </Tabs>
            </div>
        </div>
        <EntityAlreadyExistsModal
            v-if="showKeyConflictModal"
            :rp-name="lastRpName"
            :resolve-data="lastResolveData"
            @cancel="showKeyConflictModal = false"
            @resolve="onExistingEntityResolve"
            @error="onExistingEntityError"
        />
        <RevertActionModal
            v-if="showRevertModal"
            :impact-status="revertImpactStatus.status"
            :affected-entity-count="revertImpactStatus.data.affectedEntityCount || 0"
            :error-message="revertActionError"
            :reverting="isReverting"
            :insured-id="currentAction ? currentAction.insuredId : null"
            :entity-name="revertEntityName"
            @close="closeRevertModal"
            @confirm="confirmRevert"
        />
    </PageWrapper>
</template>

<style lang="scss">
.ActionsReview {
    $self: &;

    .PageWrapper {
        display: flex;
        flex-direction: column;
    }

    &__container {
        height: 100%;
        min-height: 0;
        display: flex;
        flex-direction: column;
        align-items: center;

        > * {
            width: 100%;
        }
    }

    &__tableSection {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
    }

    &__processingMessage {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: var(--eidQualityBlack);
        padding: 15px 10px;
        border-radius: var(--primary-border-radius);
        background-color: var(--eidTrustGreen-10);
        border: 1px solid var(--eidTrustGreen);
        margin-bottom: 10px;
    }

    &__processingSpinner {
        flex-shrink: 0;
        color: var(--eidTrustGreen);
    }
}
</style>

<script lang="ts">
    import { Component, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { isEqual, noop, omit } from 'lodash';

    import { faSpinner } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { Tab, Tabs } from '@evidentid/dashboard-commons/components/Tabs';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';
    import {
        hasSufficientFeatures,
    } from '@evidentid/dashboard-commons/modules/role-base-access-control/utils/has-sufficient-features/hasSuffiencientFeatures';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import {
        ActionResolveInput,
        AcceptanceActionResolveInput,
        ActionReview,
        ActionsTab,
        ActionType,
        ContractExtractionResolveInput,
        RevertImpact,
    } from '@evidentid/tprm-portal-lib/models/entity-actions-review';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import {
        deserializeSorting,
        serializeSorting,
        Sorting,
        SortingDirection,
    } from '@evidentid/universal-framework/sorting';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import { VuexCommonEntityStatus } from '@/models/VuexCommonEntityStatus.model';
    import { VuexCommonListStatus } from '@/models/VuexCommonListStatus.model';
    import { ContractExtractionState, useContractExtractionStore } from '@/modules/contract-extraction/store';
    import { DashboardState, useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import ActionReviewDetails
        from '@/modules/entity-actions-review/components/ActionReviewDetails/ActionReviewDetails.vue';
    import ActionsList from '@/modules/entity-actions-review/components/ActionsList/ActionsList.vue';
    import EntityAlreadyExistsModal
        from '@/modules/entity-actions-review/components/EntityAlreadyExistsModal/EntityAlreadyExistsModal.vue';
    import RevertActionModal
        from '@/modules/entity-actions-review/components/RevertActionModal/RevertActionModal.vue';
    import {
        ACTIONS_TAB_LABELS,
        ACTIONS_TAB_QUERY_KEY,
        COMPLETED_ONLY_FILTER_KEYS,
        REVERT_FAILED_AFTER_PAUSE_MESSAGE,
        REVERT_PAUSED_NOTE,
        REVERT_SUCCESS_MESSAGE,
    } from '@/modules/entity-actions-review/constants';
    import { revertImpactKey, useEntityActionsReviewStore } from '@/modules/entity-actions-review/store';
    import { ActionsListFilters, EntityActionScopeType } from '@/modules/entity-actions-review/types';
    import {
        readFiltersFromQuery,
        writeFiltersToQuery,
    } from '@/modules/entity-actions-review/utils/actionsListFilterQuery';
    import { getActionScopeType } from '@/modules/entity-actions-review/utils/getActionScopeType';
    import { getActionValue } from '@/modules/entity-actions-review/utils/getActionValue';
    import { usePageLayoutStore } from '@/modules/page-layout/store';

    @Component({
        components: {
            ActionReviewDetails,
            ActionsList,
            EidIcon,
            EntityAlreadyExistsModal,
            PageWrapper,
            RevertActionModal,
            Tab,
            Tabs,
        },
    })
    export default class ActionsReview extends Vue {
        private get pageLayoutStore() {
            return usePageLayoutStore();
        }

        private readonly faSpinner = faSpinner;
        private readonly defaultSorting = { column: 'createdat', direction: SortingDirection.asc };
        private lastRpName!: string;
        private currentAction: ActionReview | null = null;
        private currentActionIndex: number | null = null;
        private noEntityFound: boolean = false;
        private showKeyConflictModal: boolean = false;
        private lastResolveData: ActionResolveInput | null = null;
        private showRevertModal: boolean = false;

        /* Covers the whole confirm sequence, including the pause that precedes the revert. The store's
           `revertingActionStatus` only starts once `revertAction` does, leaving the pause window
           unguarded against a second click or a Cancel that would otherwise let the revert land anyway. */
        private revertInFlight: boolean = false;
        private previousPendingCount: number = 0;
        private reloadToken: number = 0;
        private description = 'Stay on track with an active list of items that need your attention to keep verifications moving forward.';

        private created(): void {
            this.pageLayoutStore.resetPageLayout();
        }

        private get additionalClass(): string {
            const additionalClass: string[] = [ 'ActionsReview' ];
            if (this.currentAction) {
                additionalClass.push('ActionsReview--detailsShown');
            }
            if (this.isDocumentAction) {
                additionalClass.push('ActionsReview--documentAction');
            }
            return additionalClass.join(' ');
        }

        private get rpName(): string {
            return this.$rp.current || '';
        }

        private get requirementDefinitions(): DashboardState['tprmRequirementsDefinitions'] {
            return (useDashboardStore().$state as DashboardState).tprmRequirementsDefinitions || {};
        }

        private get entitiesStatus() {
            return (useDashboardStore().$state as DashboardState).entities[this.rpName] || {
                count: Infinity,
                finished: false,
                loading: false,
                list: [],
            };
        }

        private readonly tabIds: ActionsTab[] = [ ActionsTab.pending, ActionsTab.completed ];
        private readonly tabLabels = ACTIONS_TAB_LABELS;

        private get activeTab(): ActionsTab {
            const requested = this.$route.query?.[ACTIONS_TAB_QUERY_KEY];
            return this.tabIds.includes(requested as ActionsTab)
                ? requested as ActionsTab
                : ActionsTab.pending;
        }

        private onTabChange(tab: ActionsTab): void {
            if (tab === this.activeTab) {
                return;
            }
            // The detail view belongs to the tab it was opened from, and the resolved-*
            // facets have no meaning on Pending.
            const dropped = tab === ActionsTab.pending
                ? [ 'id', ...COMPLETED_ONLY_FILTER_KEYS ]
                : [ 'id' ];
            this.$router.push({
                ...this.$route,
                query: {
                    ...omit(this.$route.query, dropped),
                    [ACTIONS_TAB_QUERY_KEY]: tab,
                },
            } as any).catch(noop);
        }

        private get entityActionsStatus() {
            return useEntityActionsReviewStore().entityActions[this.rpName]?.[this.activeTab] || {
                status: OperationStatus.uninitialized,
                list: [],
                loading: false,
                finished: false,
                total: null,
                loadKey: null,
            };
        }

        private get endorsementCategoriesStatus(): VuexCommonListStatus<string> {
            return useEntityActionsReviewStore().endorsementCategories[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get resolvingActionStatus(): OperationStatus {
            return useEntityActionsReviewStore().resolvingActionStatus[this.rpName]
                || OperationStatus.uninitialized;
        }

        private get resolveConflictError(): string | null {
            return useEntityActionsReviewStore().resolveConflictError[this.rpName] || null;
        }

        // Derived from the tab that opened the detail, not from the action's own status or
        // resolution. A reverted action carries status `reverted` but belongs back on the
        // Pending queue to be decided again, so a status check would wrongly render it
        // read-only there; and a pre-feature row has no resolution at all, so a resolution
        // check would wrongly render a genuinely completed row as editable.
        private get isCurrentActionReadOnly(): boolean {
            return this.activeTab === ActionsTab.completed;
        }

        private get revertImpactStatus(): VuexCommonEntityStatus<RevertImpact> {
            const key = this.currentAction
                ? revertImpactKey(this.rpName, this.currentAction.id)
                : '';
            return useEntityActionsReviewStore().revertImpact[key] || {
                status: OperationStatus.uninitialized,
                data: {} as RevertImpact,
            };
        }

        private get revertingActionStatus(): OperationStatus {
            return useEntityActionsReviewStore().revertingActionStatus[this.rpName]
                || OperationStatus.uninitialized;
        }

        private get isReverting(): boolean {
            return this.revertingActionStatus === OperationStatus.loading;
        }

        /* Names the entity in the revert dialog's title. Null for a global action, and null on a row
           whose entityNames the list never carried, which the dialog falls back from by counting. */
        private get revertEntityName(): string | null {
            return this.currentAction?.entityNames?.displayName || null;
        }

        private get revertActionError(): string | null {
            return useEntityActionsReviewStore().revertActionError[this.rpName] || null;
        }

        private get isLoading(): boolean {
            return (!this.currentAction && this.entitiesStatus.loading)
                || this.endorsementCategoriesStatus.status === OperationStatus.loading
                || this.resolvingActionStatus === OperationStatus.loading;
        }

        private get sorting(): Sorting {
            return deserializeSorting(this.$route.query.sort) || this.defaultSorting;
        }

        private get isFirstAction(): boolean {
            return this.currentActionIndex === 0;
        }

        private get isLastAction(): boolean {
            return this.currentActionIndex === this.entityActionsStatus.list.length - 1;
        }

        private get filters(): ActionsListFilters {
            return readFiltersFromQuery(this.$route.query as Record<string, unknown>, this.activeTab);
        }

        private onFiltersChange(filters: ActionsListFilters): void {
            this.$router.push({
                ...this.$route,
                query: omit(writeFiltersToQuery(this.$route.query as Record<string, unknown>, filters), 'id'),
            } as any).catch(noop);
        }

        private get entityName(): string {
            return this.filteredByEntity?.displayName || 'Entity';
        }

        private get filteredByEntity(): Entity | null {
            if (!this.filters.entityId) {
                return null;
            }
            return this.entitiesStatus.list.find((x) => this.filters.entityId === x.id) || null;
        }

        private get noActionMessage(): string | null {
            if (!this.filters.entityId) {
                return null;
            }
            if (this.activeTab === ActionsTab.completed) {
                // The scope notice rendered alongside this message already explains where a
                // resolved global action for this entity went, so this describes only the
                // current, empty result set rather than repeating that destination.
                return 'No completed actions for this entity.';
            }
            return this.filteredByEntity
                ? `All available actions for ${this.filteredByEntity.displayName} have been completed.`
                : `There are no available actions to be completed for this entity.`;
        }

        private get isDocumentAction(): boolean {
            return [ ActionType.documentDataReview, ActionType.contractExtraction ]
                .includes(this.currentAction?.action.$action as ActionType);
        }

        private get contractExtractionState(): ContractExtractionState {
            return useContractExtractionStore().$state as ContractExtractionState;
        }

        private get pendingExtractions() {
            return this.contractExtractionState.pendingExtractions[this.rpName] || [];
        }

        private get hasProcessingContracts(): boolean {
            return this.pendingExtractions.length > 0;
        }

        private get processingContractsMessage(): string {
            const count = this.pendingExtractions.length;
            return count === 1
                ? '1 contract is currently processing'
                : `${count} contracts are currently processing`;
        }

        @Watch('isLoading', { immediate: true })
        private onLoadingChange(): void {
            this.pageLayoutStore.setLoading(this.isLoading);
        }

        @Watch('$rp.current', { immediate: true })
        private async handleRpChange(rpName: string): Promise<void> {
            // Save information about the last resource, which should be cleared during destroy
            this.lastRpName = rpName;
            if (rpName && rpName !== this.lastRpName) {
                await useRbacStore().validateRbac(
                    [ RbacFeature.actionsViewMainActionsList ],
                );
            }
            this.previousPendingCount = this.pendingExtractions.length;
            this.loadEntity();
            this.setCurrentActionAndIndex();
            this.loadEndorsementCategories();
        }

        @Watch('$route.query')
        private handleQueryChange(current: any, previous: any) {
            if (!isEqual(omit(current, 'id'), omit(previous, 'id'))) {
                this.loadEntity();
            }
            if (current.id !== previous.id) {
                this.setCurrentActionAndIndex();
            }
        }

        @Watch('entityActionsStatus', { immediate: true })
        private onEntityActionsStatusChange(): void {
            if (this.entityActionsStatus.status === OperationStatus.success &&
                this.entityActionsStatus.list.length > 0 &&
                this.$route.query?.id) {
                this.setCurrentActionAndIndex();
            }
        }

        @Watch('pendingExtractions')
        private onPendingExtractionsChange(): void {
            const currentCount = this.pendingExtractions.length;
            if (currentCount === 0 && this.previousPendingCount > 0) {
                this.reloadToken += 1;
            }
            this.previousPendingCount = currentCount;
        }

        private setCurrentActionAndIndex(): void {
            const actionId = this.$route.query?.id || null;
            const index = this.entityActionsStatus.list.findIndex((x: ActionReview) => x.id === actionId);
            this.currentActionIndex = index >= 0 ? index : null;
            this.currentAction = index >= 0
                ? this.entityActionsStatus.list[index]
                : null;
        }

        private navigateToDetails(id: string): void {
            this.$router.push({
                ...this.$route,
                query: { ...this.$route.query, id },
            } as any).catch(noop);
        }

        private navigateBack(): void {
            this.$router.push({
                ...this.$route,
                query: {
                    ...omit(this.$route.query, 'id'),
                },
            } as any).catch(noop);
        }

        private async loadEntity(): Promise<void> {
            if (this.filters.entityId && !this.entitiesStatus.loading) {
                try {
                    await useDashboardStore().loadEntity({
                        rpName: this.rpName,
                        id: this.filters.entityId,
                    });
                    this.noEntityFound = false;
                } catch (err) {
                    this.noEntityFound = true;
                }
            }
        }

        private async loadEndorsementCategories(): Promise<void> {
            if (this.endorsementCategoriesStatus.status !== OperationStatus.loading) {
                await useEntityActionsReviewStore().loadEndorsementCategories({
                    rpName: this.rpName,
                });
            }
        }

        private onSortChange(sort: Sorting): void {
            this.$router.push({
                ...this.$route,
                query: { ...this.$route.query, sort: serializeSorting(sort) },
            } as any);
        }

        private nextAction(): void {
            if (this.currentActionIndex != null && !this.isLastAction) {
                this.navigateToDetails(this.entityActionsStatus.list[this.currentActionIndex + 1].id);
            }
        }

        private previousAction(): void {
            if (this.currentActionIndex != null && !this.isFirstAction) {
                this.navigateToDetails(this.entityActionsStatus.list[this.currentActionIndex - 1].id);
            }
        }

        private canResolveAction(): boolean {
            if (this.currentAction) {
                const isGlobal = getActionScopeType(this.currentAction.action) === EntityActionScopeType.global;
                const userFeatures = useUserStore().currentRpRbacFeatures;
                const requiredFeatures = isGlobal
                    ? [ RbacFeature.actionsResolveGlobalAction ]
                    : [ RbacFeature.actionsResolveEntityAction ];
                return hasSufficientFeatures(requiredFeatures, userFeatures);
            }
            return false;
        }

        private async tryResolveAction(data: ActionResolveInput): Promise<void> {
            // A Completed row's action is already decided. Nothing in the detail view
            // itself knows which tab opened it, so this is the one place left to refuse a
            // resolve rather than let an admin re-resolve (and, for a reverted row, silently
            // succeed at re-resolving) something that already belongs on that tab.
            if (this.activeTab === ActionsTab.completed) {
                return;
            }
            if (this.canResolveAction()) {
                await this.resolveAction(data);
            }
        }

        private navigateAfterResolve(nextAction: ActionReview | null): void {
            this.currentActionIndex = null;
            this.currentAction = null;
            if (nextAction) {
                this.navigateToDetails(nextAction.id);
            } else {
                this.navigateBack();
            }
        }

        private async resolveAction(data: ActionResolveInput): Promise<void> {
            if (this.currentAction && this.currentActionIndex != null) {
                const actionTobeResolved = this.currentAction;
                const nextAction = this.isLastAction && this.isFirstAction
                    ? null
                    : this.isLastAction
                        ? this.entityActionsStatus.list[this.currentActionIndex - 1]
                        : this.entityActionsStatus.list[this.currentActionIndex + 1];
                await useEntityActionsReviewStore().resolveEntityAction({
                    rpName: this.lastRpName,
                    tab: this.activeTab,
                    actionId: this.currentAction.id,
                    resolve: data,
                });
                if (this.resolvingActionStatus === OperationStatus.success) {
                    // The action just left Pending and now belongs on Completed, whose cached list does not
                    // know that. Without this the reviewer switches tabs and cannot find the action they
                    // just decided, because the tab watcher skips reloading a list it already has. Mirrors
                    // what confirmRevert does to the Pending tab for the opposite transition.
                    useEntityActionsReviewStore().clearEntityActionsTab({
                        rpName: this.rpName,
                        tab: ActionsTab.completed,
                    });
                    this.showResolvedAlert(actionTobeResolved, data);
                    this.navigateAfterResolve(nextAction);
                } else if (this.resolvingActionStatus === OperationStatus.error) {
                    if (this.resolveConflictError === 'Conflict') {
                        useEntityActionsReviewStore().finishResolvedAction({
                            rpName: this.lastRpName,
                            tab: this.activeTab,
                            actionId: actionTobeResolved.id,
                        });
                        useSnackbarStore().displaySnackbar({
                            success: true,
                            message: 'This action has already been resolved.',
                        });
                        this.navigateAfterResolve(nextAction);
                    } else if (this.resolveConflictError === 'KeyConflict') {
                        this.lastResolveData = data;
                        this.showKeyConflictModal = true;
                    } else {
                        useSnackbarStore().displaySnackbar({
                            success: false,
                            message: 'Error resolving the action',
                        });
                    }
                }
            }
        }

        private async onExistingEntityResolve(data: ActionResolveInput): Promise<void> {
            this.showKeyConflictModal = false;
            await this.tryResolveAction(data);
        }

        private onExistingEntityError(message: string): void {
            this.showKeyConflictModal = false;
            useSnackbarStore().displaySnackbar({
                success: false,
                message,
            });
        }

        private async openRevertModal(): Promise<void> {
            if (!this.currentAction) {
                return;
            }
            this.showRevertModal = true;
            const actionId = this.currentAction.id;
            useEntityActionsReviewStore().setRevertActionError({ rpName: this.rpName, error: null });
            await useEntityActionsReviewStore().loadRevertImpact({ rpName: this.rpName, actionId });
            // The dialog can be dismissed while this is in flight, and closing it clears the impact entry
            // before the response arrives to write it back. Clearing again once it lands keeps those
            // entries from accumulating for the life of the session as the reviewer moves between actions.
            if (!this.showRevertModal) {
                useEntityActionsReviewStore().clearRevertImpact({ rpName: this.rpName, actionId });
            }
        }

        private closeRevertModal(): void {
            if (this.currentAction) {
                useEntityActionsReviewStore().clearRevertImpact({
                    rpName: this.rpName,
                    actionId: this.currentAction.id,
                });
            }
            this.showRevertModal = false;
        }

        private async confirmRevert(payload: { reason: string | null, pauseEntity: boolean }): Promise<void> {
            // The pause runs before the revert, and neither the confirm button's `reverting` state nor the
            // dialog's progress state covers that window, so this guards it: without it a second click
            // starts a second pause-and-revert, and a Cancel during the pause lets the revert land anyway.
            if (!this.currentAction || this.revertInFlight) {
                return;
            }
            this.revertInFlight = true;
            try {
                await this.runRevert(payload);
            } finally {
                this.revertInFlight = false;
            }
        }

        private async runRevert(payload: { reason: string | null, pauseEntity: boolean }): Promise<void> {
            const actionId = this.currentAction!.id;
            const insuredId = this.currentAction!.insuredId;
            // The pause goes first and a failure stops the revert. Reverting anyway would send exactly
            // the reminders the reviewer ticked the box to prevent, and the decision is easier to
            // withdraw a second time than an email is to unsend.
            let pausedHere = false;
            if (payload.pauseEntity && insuredId) {
                pausedHere = await useEntityActionsReviewStore().pauseEntity({ rpName: this.rpName, insuredId });
                if (!pausedHere) {
                    return;
                }
            }
            await useEntityActionsReviewStore().revertAction({
                rpName: this.rpName,
                actionId,
                ...(payload.reason ? { reason: payload.reason } : {}),
            });
            if (this.revertingActionStatus === OperationStatus.success) {
                this.closeRevertModal();
                // The store patches the reverted row into the Completed tab's cached list
                // directly, but the Pending tab's cached list and total go stale here: the
                // backend's fan-out may re-link this action there. actionCount is left
                // untouched -- that badge counts pending work, and the action is not
                // re-linked to the queue yet.
                useEntityActionsReviewStore().clearEntityActionsTab({
                    rpName: this.rpName,
                    tab: ActionsTab.pending,
                });
                useSnackbarStore().displaySnackbar({
                    success: true,
                    message: REVERT_SUCCESS_MESSAGE,
                });
            } else if (pausedHere) {
                // The pause already committed, so the entity is suppressed even though the decision it was
                // meant to accompany still stands. Saying so is the only way the reviewer learns they now
                // have an entity to unpause. Where the revert failed for a reason worth reading -- a 409
                // that says it is no longer revertable, a 403 that says they lack permission -- that
                // reason is kept and the pause note added to it, since telling them only to try again
                // would send them at a retry that fails the same way.
                const specificError = this.revertActionError;
                useEntityActionsReviewStore().setRevertActionError({
                    rpName: this.rpName,
                    error: specificError
                        ? `${specificError} ${REVERT_PAUSED_NOTE}`
                        : REVERT_FAILED_AFTER_PAUSE_MESSAGE,
                });
            }
        }

        private showResolvedAlert(
            resolvedAction: ActionReview,
            resolveData: ActionResolveInput,
        ): void {
            const actionVerb = (resolveData as AcceptanceActionResolveInput).accepted
                ? 'accepted'
                : 'rejected';
            let alertMessage = resolvedAction.action.$action === ActionType.entityNameMatchingV1
                ? `${getActionValue(resolvedAction)} ${actionVerb}`
                : `${getActionValue(resolvedAction)} was saved and removed`;

            const isContractExtractionDecline = resolvedAction.action.$action === ActionType.contractExtraction
                && (resolveData as ContractExtractionResolveInput).riskProfile === null;
            if (isContractExtractionDecline) {
                alertMessage = `Action successfully declined`;
            }
            useSnackbarStore().displaySnackbar({
                success: true,
                message: alertMessage,
            });
        }

        private removeFilter(filter: string[]) {
            this.$router.push({
                ...this.$route,
                query: omit(this.$route.query, filter),
            } as any).catch(noop);
        }

        private unmounted() {
            useDashboardStore().clearEntitiesList({ rpName: this.lastRpName });
            useEntityActionsReviewStore().clearEntityActions({ rpName: this.lastRpName });
            useEntityActionsReviewStore().clearResolvingEntityActionStatus({ rpName: this.lastRpName });
            useEntityActionsReviewStore().clearEndorsementCategories({ rpName: this.lastRpName });
            // Leaves nothing behind for whichever action the revert modal was open on when
            // navigation tore this view down mid-flight.
            if (this.currentAction) {
                useEntityActionsReviewStore().clearRevertImpact({
                    rpName: this.lastRpName,
                    actionId: this.currentAction.id,
                });
            }
            useEntityActionsReviewStore().clearRevertingActionStatus({ rpName: this.lastRpName });
        }
    }
</script>
